import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { 
  useCreateReportSetting, 
  useUpdateReportSetting,
  type ReportSetting,
  type AdjustedMarginReportOptionMonthFilters,
  validateSettingValue
} from "@/lib/api/reportSettings";
import { OptionMonthOptions } from "@/lib/api/common";

interface ReportSettingFormProps {
  mode: "create" | "update";
  initialData?: ReportSetting;
  onSuccess: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

export function ReportSettingForm({
  mode,
  initialData,
  onSuccess,
  onCancel,
  showCancelButton = true,
}: ReportSettingFormProps) {
  const { toast } = useToast();
  const createReportSetting = useCreateReportSetting();
  const updateReportSetting = useUpdateReportSetting();

  const [optionMonths, setOptionMonths] = useState<string[]>(mode === "update" && initialData?.setting_value 
    ? (initialData.setting_value as AdjustedMarginReportOptionMonthFilters).option_months || []
    : []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update option months when initial data changes
  useEffect(() => {
    if (mode === "update" && initialData?.setting_value) {
      const value = initialData.setting_value as AdjustedMarginReportOptionMonthFilters;
      setOptionMonths(value.option_months || []);
    }
  }, [mode, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (optionMonths.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one option month",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build the setting value following the schema
      const settingValue: AdjustedMarginReportOptionMonthFilters = {
        filter_type: "blacklist",
        commodity_type: null, // Global
        row_type: "option", // Option only
        option_months: optionMonths,
      };

      // Validate the setting value
      try {
        validateSettingValue("dorman", "adjusted_margin_report_option_month_filters", settingValue);
      } catch (validationError: any) {
        toast({
          title: "Validation Error",
          description: validationError.message || "Invalid setting value",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      let result;
      if (mode === "create") {
        result = await createReportSetting({
          provider_key: "dorman",
          setting_key: "adjusted_margin_report_option_month_filters",
          setting_value: settingValue,
        });
      } else {
        if (!initialData?.id) {
          toast({
            title: "Error",
            description: "Missing report setting ID",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }

        result = await updateReportSetting({
          id: initialData.id,
          provider_key: "dorman",
          setting_key: "adjusted_margin_report_option_month_filters",
          setting_value: settingValue,
        });
      }

      if (result.success) {
        toast({
          title: mode === "create" ? "Created" : "Updated",
          description: `Report setting has been ${mode === "create" ? "created" : "updated"} successfully`,
        });
        onSuccess();
        
        // Reset form if creating
        if (mode === "create") {
          setOptionMonths([]);
        }
      } else {
        toast({
          title: "Error",
          description: result.message || `Failed to ${mode} report setting`,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || `Failed to ${mode} report setting`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Fixed Fields Display */}
      <div className="space-y-4 p-4 bg-muted rounded-md">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium">Provider</Label>
            <p className="text-sm text-muted-foreground mt-1">Dorman</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Filter Type</Label>
            <p className="text-sm text-muted-foreground mt-1">Blacklist</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Commodity</Label>
            <p className="text-sm text-muted-foreground mt-1">All (Global)</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Row Type</Label>
            <p className="text-sm text-muted-foreground mt-1">Option</p>
          </div>
        </div>
      </div>

      {/* Option Months Selection */}
      <div className="space-y-2 grid w-full">
        <Label htmlFor="option-months" className="text-sm font-medium">
          Option Months to Blacklist <span className="text-destructive">*</span>
        </Label>
        <ComboboxMulti
          isModal
          placeholder="Option months..."
          value={optionMonths}
          onValueChange={setOptionMonths}
          options={OptionMonthOptions}
          dwimOnChange
        />
        <p className="text-sm text-muted-foreground">
          Select the option months that should be filtered out (blacklisted) from reports
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {showCancelButton && onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {mode === "create" ? "Create" : "Update"} Setting
        </Button>
      </div>
    </form>
  );
}
